import React, { useState, useEffect } from "react";
import { Stepper } from "@shared/components/Stepper";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import BasicInfo from "./steps/BasicInfo";
import PlanConfiguration from "./steps/PlanConfiguration";
import SelectRates from "./steps/SelectRates";
import { ChargesTaxes } from "./steps/ChargesTaxes";
import Penalties from "./steps/Penalties";
import { Deposits } from "./steps/Deposits";
import Review from "./steps/Review";
import { CreateReview } from "./steps/CreateReview";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";

const CreatePlanStepper: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { action, planId } = useParams();

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNav, setPendingNav] = useState<string | null>(null);

  useEffect(() => {
    if (action !== 'edit') return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      
      if (anchor && anchor.href && anchor.getAttribute('target') !== '_blank') {
        const isSamePage = anchor.href === window.location.href;
        if (!isSamePage && !anchor.href.includes('#')) {
          e.preventDefault();
          e.stopPropagation();
          setPendingNav(anchor.pathname + anchor.search);
          setShowUnsavedDialog(true);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleClick, { capture: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [action]);

  const handleLeaveAnyway = () => {
    localStorage.removeItem(`plan-edit-progress-${planId}`);
    setShowUnsavedDialog(false);
    if (pendingNav) {
      navigate(pendingNav);
    }
  };

  const handleBackClick = () => {
    if (action === 'edit') {
      setPendingNav("/plan-tarrif/plan-tarrif-landing?tab=plans");
      setShowUnsavedDialog(true);
    } else {
      navigate("/plan-tarrif/plan-tarrif-landing?tab=plans");
    }
  };

  const handleComplete = async (allStepData: any) => {
    localStorage.removeItem(`plan-edit-progress-${planId}`);
    toast({
      title: "Plan Created Successfully",
    });

    navigate("/plan-tarrif/plan-tarrif-landing?tab=plans");
  };

  const stepperSteps = [
    {
      id: "basic-info",
      title: "Basic Info",
      component: <BasicInfo/>,
      slug: "basic-info",
    },
    {
      id: "plan-config",
      title: "Plan Configuration",
      component: <PlanConfiguration/>,
      slug: "plan-config",
    },
    {
      id: "select-rates",
      title: "Select Rates",
      component:<SelectRates/>,
      slug: "select-rates",
    },
    {
      id: "charges-taxes",
      title: "Charges & Taxes",
      component: <ChargesTaxes/>,
      slug: "charges-taxes",
    },
    {
      id: "deposits",
      title: "Deposits",
      component: <Deposits />,
      slug: "deposits",
    },
    {
      id: "penalties",
      title: "Penalties",
      component: <Penalties/>,
      slug: "penalties",
    },
    {
      id: "review-pricing",
      title: "Review & Pricing",
      component: action === 'edit' ? <Review/> : <CreateReview/>,
      slug: "review-pricing",
    },
  ];

  return (
    <div className="">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <PageHeader
          title={action === 'edit' || action === 'draftedit' ? "Edit Plan" : "Create New Plan"}
          subtitle="Bundle one or more rates into a plan, configure billing rules, and assign consumers."
        />
      </div>

      <Stepper
        steps={stepperSteps}
        onComplete={handleComplete}
        persistProgress={action === 'edit'}
        storageKey={action === 'edit' ? `plan-edit-progress-${planId}` : undefined}
        enableApiSave={false}
        saveOnStepChange={false}
        trackDirtyState={false}
        autoSave={action === 'edit'}
      />

      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. To save changes, please stay on the page and click "Save Changes" on the Review step. 
              Leaving now will discard your current edits.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowUnsavedDialog(false)}>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeaveAnyway} className="bg-red-600 hover:bg-red-600 text-white">
              Leave Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CreatePlanStepper;
